import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    X, AlertCircle, AlertTriangle, CheckCircle2, Loader2, Eye, Save, ArrowLeft, History,
} from 'lucide-react';
import {
    columnMappingApi, documentToDraft, draftToRequest, isDirty, suggestSources,
} from '../api/columnMappingApi';

/**
 * Map the columns of one uploaded file to the attributes the engine reads.
 *
 * Left is ours and fixed — those names come from the engine's registry, so they
 * cannot be typed or renamed here. Right is whatever the uploaded file actually
 * contains, chosen from its real headings.
 *
 * <h3>Why saving is behind a preview</h3>
 * A mapping that fails loudly is not the danger; the run stops and someone
 * fixes it. The danger is one that fails quietly — a rate pointed at a column
 * of names reads as 0.00 rather than as an error, and a zero rate is a figure
 * the engine honours. So the server refuses the cases it can prove wrong, and
 * for the rest — a referrer rate pointed at the subcontractor's column, where
 * every value is a valid rate — the only control is a person looking at real
 * rows. That is what the preview is, and why it is not optional here.
 */
export default function ColumnMappingDrawer({
    orgUuid, year, month, fileType, fileLabel, hasFile, onClose, onSaved,
}) {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [registry, setRegistry] = useState(null);
    const [current, setCurrent] = useState(null);
    const [headers, setHeaders] = useState(null);
    const [headerError, setHeaderError] = useState(null);

    const [draft, setDraft] = useState(null);
    const [original, setOriginal] = useState(null);
    const [note, setNote] = useState('');

    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const [versions, setVersions] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    // ── Load ────────────────────────────────────────────────────────────

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError(null);

        Promise.all([
            columnMappingApi.getRegistry(orgUuid),
            columnMappingApi.getCurrent(orgUuid),
        ])
            .then(([reg, cur]) => {
                if (cancelled) return;
                setRegistry(reg);
                setCurrent(cur);
                const loaded = documentToDraft(cur.document);
                setDraft(loaded);
                setOriginal(loaded);
            })
            .catch((err) => {
                if (!cancelled) setLoadError(messageOf(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [orgUuid]);

    // The uploaded file's real headings. Its absence is not an error — it is
    // the ordinary state before anything has been uploaded — so it is held
    // separately and reported as guidance rather than as a failure.
    useEffect(() => {
        if (!hasFile) {
            setHeaders(null);
            setHeaderError(null);
            return undefined;
        }
        let cancelled = false;
        columnMappingApi.getSourceHeaders(orgUuid, year, month, fileType)
            .then((h) => { if (!cancelled) { setHeaders(h); setHeaderError(null); } })
            .catch((err) => { if (!cancelled) setHeaderError(messageOf(err)); });
        return () => { cancelled = true; };
    }, [orgUuid, year, month, fileType, hasFile]);

    const file = useMemo(
        () => registry?.files?.find((f) => f.type === fileType) ?? null,
        [registry, fileType],
    );

    // Worded per file: two of the four have no columns to choose from at all,
    // and telling their operator to pick one is simply wrong.
    const nothingUploaded = !hasFile && file
        ? `Upload this file first to choose from the ${sourceNoun(file.shape)} it actually contains.`
        : null;

    const section = draft?.[fileType] ?? { columns: {}, labels: {} };
    const dirty = draft && original && isDirty(draft, original);

    // Any edit invalidates the preview: a preview of a mapping you have since
    // changed is worse than none, because it looks like evidence.
    const setSource = useCallback((group, fieldId, value) => {
        setDraft((prev) => ({
            ...prev,
            [fileType]: {
                ...prev[fileType],
                [group]: { ...prev[fileType][group], [fieldId]: value },
            },
        }));
        setPreview(null);
        setSaveError(null);
    }, [fileType]);

    // ── Actions ─────────────────────────────────────────────────────────

    const runPreview = async () => {
        setPreviewing(true);
        setPreviewError(null);
        try {
            setPreview(await columnMappingApi.preview(
                orgUuid, year, month, fileType, draftToRequest(draft, note)));
        } catch (err) {
            setPreviewError(messageOf(err));
            setPreview(null);
        } finally {
            setPreviewing(false);
        }
    };

    const save = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const saved = await columnMappingApi.save(orgUuid, draftToRequest(draft, note));
            setOriginal(draft);
            onSaved?.(saved);
            onClose();
        } catch (err) {
            setSaveError(messageOf(err));
        } finally {
            setSaving(false);
        }
    };

    const openHistory = async () => {
        setShowHistory(true);
        if (versions) return;
        try {
            setVersions(await columnMappingApi.listVersions(orgUuid));
        } catch (err) {
            setVersions([]);
            setSaveError(messageOf(err));
        }
    };

    const canSave = dirty && !saving && preview?.canActivate === true;
    const previewProblems = preview?.problems ?? [];

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <button
                aria-label="Close"
                className="absolute inset-0 bg-slate-900/40"
                onClick={onClose}
            />
            <div
                role="dialog"
                aria-label={`Column mapping for ${fileLabel}`}
                className="relative w-full max-w-3xl bg-white h-full shadow-xl flex flex-col"
            >
                <Header
                    fileLabel={fileLabel}
                    current={current}
                    onClose={onClose}
                    onHistory={openHistory}
                />

                {loading && <Centered><Loader2 className="animate-spin" /> Loading…</Centered>}
                {loadError && <Notice tone="error">{loadError}</Notice>}

                {showHistory && (
                    <VersionHistory versions={versions} onBack={() => setShowHistory(false)} />
                )}

                {!loading && !loadError && !showHistory && file && (
                    <>
                        <div className="flex-1 overflow-y-auto">
                            <Intro
                                file={file}
                                headers={headers}
                                headerError={headerError ?? nothingUploaded}
                            />

                            {file.columns.length > 0 && (
                                <FieldGroup
                                    oursLabel="System attribute"
                                    theirsLabel="Column in your spreadsheet"
                                    title="Columns"
                                    hint="Matched by heading, so a column moving left or right upstream does not matter."
                                    fields={file.columns}
                                    values={section.columns}
                                    options={headers?.columns ?? []}
                                    onChange={(id, v) => setSource('columns', id, v)}
                                    problems={preview?.problems}
                                />
                            )}

                            {file.labels.length > 0 && (
                                <FieldGroup
                                    oursLabel="System total"
                                    theirsLabel="Row label in your invoice"
                                    title="Invoice rows"
                                    hint="This file is an invoice, not a table: each figure is found by the text its row starts with."
                                    fields={file.labels}
                                    values={section.labels}
                                    options={headers?.labels ?? []}
                                    onChange={(id, v) => setSource('labels', id, v)}
                                    problems={preview?.problems}
                                />
                            )}

                            {preview && <PreviewPanel preview={preview} file={file} />}
                            {previewError && <Notice tone="error">{previewError}</Notice>}
                            {saveError && <Notice tone="error">{saveError}</Notice>}
                        </div>

                        <Footer
                            dirty={dirty}
                            hasFile={hasFile}
                            note={note}
                            onNote={setNote}
                            previewing={previewing}
                            onPreview={runPreview}
                            canSave={canSave}
                            saving={saving}
                            onSave={save}
                            preview={preview}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

// ── Pieces ──────────────────────────────────────────────────────────────

/**
 * `current` whole rather than its fields picked apart: before it loads, a
 * missing `builtIn` is falsy, and the subtitle confidently read
 * "Version undefined in force" — a claim about which mapping is running, made
 * before anything was known. Not knowing yet has to be its own state.
 */
function Header({ fileLabel, current, onClose, onHistory }) {
    return (
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
            <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-slate-900">
                    Column mapping · {fileLabel}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                    {!current && 'Loading…'}
                    {current?.builtIn
                        && 'Built-in default (v1) — nothing has been changed yet'}
                    {current && !current.builtIn
                        && `Version ${current.version} in force`}
                </p>
            </div>
            <button
                onClick={onHistory}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
            >
                <History size={14} /> History
            </button>
            <button
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
            >
                <X size={18} />
            </button>
        </div>
    );
}

function Intro({ file, headers, headerError }) {
    return (
        <div className="px-5 py-4 border-b border-slate-100">
            <p className="text-sm text-slate-600">
                <span className="font-medium text-sky-700">Blue</span>
                {' is the processing engine\'s names, which cannot be changed here. '}
                <span className="font-medium text-violet-700">Violet</span>
                {` is the ${sourceNoun(file.shape)} of the file you provide. `}
                {file.shape === 'BOTH'
                    && 'This workbook is both: a table of loans on its Data_ sheet, and an invoice on its Inv_ sheet, each converted and read separately. '}
                {/* The instruction is the point of the screen, so it is stated
                    rather than left to be inferred from the colours. */}
                <span className="text-slate-700">
                    If one of them changes, update the mapping here and save.
                </span>
            </p>
            {headerError && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                    {headerError}
                </p>
            )}
            {headers?.readFile && (
                <p className="mt-2 text-xs text-slate-500">
                    Reading the <span className="font-mono">{headers.readFile}</span> file
                    converted from your workbook
                    {headers.files?.length > 1
                        && ` (${headers.files.join(', ')} were converted)`}
                </p>
            )}
        </div>
    );
}

/**
 * The two sides, side by side.
 *
 * <p>Blue is ours and fixed; violet is theirs and editable. The legend is the
 * whole point: without it the two columns are just text beside text, and which
 * one is the engine's name and which came out of the spreadsheet is exactly
 * what an operator has to be sure of before changing anything.
 *
 * <p><b>Not green and red, and not amber either.</b> Red already means "this is
 * wrong" here and amber already means "look at this" — a suggestion, a warning.
 * A column painted in either would leave the real thing nothing to stand out
 * against. Blue and violet are the two hues this panel does not otherwise use,
 * so they can carry identity without also carrying a verdict.
 *
 * <p>Both are washes rather than fills, so a saturated element sitting on top —
 * a suggestion, a problem, a required marker — still reads as louder than the
 * column it is in. That is what makes red usable for "required" and "wrong"
 * without either being lost in the wash behind it.
 */
const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_1.75rem_minmax(0,1fr)] items-center gap-2';

function FieldGroup({ title, hint, fields, values, options, onChange, problems,
                     oursLabel, theirsLabel }) {
    return (
        <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500 mt-0.5 mb-3">{hint}</p>

            <div className={`${ROW_GRID} mb-2 pb-1.5 border-b border-slate-200`}>
                <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-sky-700">
                    {oursLabel}
                </span>
                <span aria-hidden="true" />
                <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-violet-700">
                    {theirsLabel}
                </span>
            </div>

            <ul className="space-y-1.5">
                {fields.map((field) => (
                    <FieldRow
                        key={field.id}
                        field={field}
                        value={values?.[field.id] ?? ''}
                        options={options}
                        onChange={(v) => onChange(field.id, v)}
                        problem={(problems ?? []).find((p) => p.fieldId === field.id) ?? null}
                    />
                ))}
            </ul>
        </div>
    );
}

/**
 * One link: our attribute, an arrow, their column.
 *
 * A suggestion is shown when the field is unmapped and a heading plausibly
 * matches, but it is never applied for the operator — the failure this feature
 * risks is a confident wrong link, so agreeing to one has to be an act.
 */
function FieldRow({ field, value, options, onChange, problem }) {
    const unmapped = !value;
    const suggestion = unmapped && options.length
        ? suggestSources(field, options)[0] ?? null
        : null;
    // The current value is rendered as an option whenever the list does not
    // already contain it. Without this, before a file is uploaded there are no
    // options at all and every field renders blank — so a mapping that is
    // entirely fine looks completely unmapped, which is the first thing an
    // operator would see and the worst possible first impression of it.
    const valueNotListed = value && !options.includes(value);
    // Only a real discrepancy once we know what the file contains.
    const missing = valueNotListed && options.length > 0;

    return (
        <li className={ROW_GRID}>
            <div className="min-w-0 border-l-2 border-sky-300 bg-sky-50/50 py-1 pl-2 rounded-r">
                <span className="text-sm text-slate-800">{field.label}</span>
                {field.required && (
                    <span
                        className="ml-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-rose-600"
                        title="Required — the run stops without it"
                    >
                        required
                    </span>
                )}
            </div>

            <div className="text-center text-slate-300 select-none" aria-hidden="true">←</div>

            <div className="min-w-0">
                <select
                    aria-label={`Column for ${field.label}`}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={`w-full text-sm rounded-md border px-2 py-1.5 font-mono
                        ${problem && problem.severity === 'BLOCKING'
                            // A problem outranks the legend: filled, not just
                            // outlined, so it still reads as wrong on a side
                            // that is red by default.
                            ? 'border-rose-500 bg-rose-100 text-rose-900'
                            : 'border-violet-300 bg-violet-50/60 text-slate-800'}`}
                >
                    <option value="">
                        {field.required ? '— not mapped —' : '— not in this file —'}
                    </option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                    {valueNotListed && (
                        <option value={value}>
                            {value}{missing ? ' (not in this file)' : ''}
                        </option>
                    )}
                </select>

                {missing && (
                    <p className="text-xs text-amber-700 mt-0.5">
                        Mapped to “{value}”, which this file does not contain.
                    </p>
                )}
                {suggestion && (
                    <button
                        type="button"
                        onClick={() => onChange(suggestion.heading)}
                        className="mt-0.5 text-xs font-medium text-amber-900 bg-amber-50 border border-amber-400 rounded px-1.5 py-0.5 hover:bg-amber-100"
                    >
                        Suggested: {suggestion.heading} — use it
                    </button>
                )}
                {problem && (
                    <p className={`text-xs mt-0.5 ${problem.severity === 'BLOCKING' ? 'text-rose-700' : 'text-amber-700'}`}>
                        {problem.message}
                    </p>
                )}
            </div>
        </li>
    );
}

/**
 * The five sampled rows, as this mapping would read them.
 *
 * Deliberately shows the values themselves. A masked preview cannot answer the
 * question it exists for — whether a name column is the right name column.
 */
function PreviewPanel({ preview, file }) {
    const fields = file.columns ?? [];
    // Defaulted, not assumed. The server omitted "problems" entirely when a
    // preview found none, and reading .length off undefined unmounted the whole
    // route — a blank white page, from the one case that meant everything was
    // fine. The server is fixed; this makes the same mistake survivable.
    const problems = preview.problems ?? [];
    const rows = preview.rows ?? [];
    return (
        <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">
                What this mapping reads
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 mb-3">
                {!preview.rowsRead
                    ? 'No rows with content were found to sample.'
                    : `First ${preview.rowsRead} row${preview.rowsRead === 1 ? '' : 's'} of `}
                {preview.readFile && (
                    <span className="font-mono">{preview.readFile}</span>
                )}
            </p>

            {problems.length === 0 ? (
                <p className="text-xs text-emerald-700 flex items-center gap-1.5">
                    <CheckCircle2 size={14} /> Nothing looks wrong. Check the values below
                    against the file before saving.
                </p>
            ) : (
                <ul className="space-y-1 mb-3">
                    {problems.map((p, i) => (
                        <li
                            key={`${p.fieldId}-${i}`}
                            className={`text-xs flex items-start gap-1.5 ${
                                p.severity === 'BLOCKING' ? 'text-rose-700' : 'text-amber-700'}`}
                        >
                            {p.severity === 'BLOCKING'
                                ? <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                                : <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />}
                            <span>{p.message}</span>
                        </li>
                    ))}
                </ul>
            )}

            {rows.length > 0 && (
                <div className="mt-3 overflow-x-auto border border-slate-200 rounded-md">
                    <table className="text-xs w-full">
                        <thead className="bg-sky-50/70">
                            <tr>
                                <th className="px-2 py-1.5 text-left font-medium text-slate-400">Row</th>
                                {/* Our names heading their values: the same
                                    pairing as the editor above, so the preview
                                    reads as an answer to it. */}
                                {fields.map((f) => (
                                    <th key={f.id} className="px-2 py-1.5 text-left font-medium text-sky-800 whitespace-nowrap">
                                        {f.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((row) => (
                                <tr key={row.rowNumber}>
                                    <td className="px-2 py-1.5 text-slate-400 font-mono">{row.rowNumber}</td>
                                    {fields.map((f) => (
                                        <td key={f.id} className="px-2 py-1.5 text-slate-700 whitespace-nowrap">
                                            {row.values?.[f.id] || <span className="text-slate-300">—</span>}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Footer({
    dirty, hasFile, note, onNote, previewing, onPreview, canSave, saving, onSave, preview,
}) {
    return (
        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50">
            {dirty && (
                <input
                    value={note}
                    onChange={(e) => onNote(e.target.value)}
                    placeholder="Why did this change? (optional)"
                    maxLength={500}
                    className="w-full text-sm border border-slate-300 rounded-md px-2.5 py-1.5 mb-2"
                />
            )}
            <div className="flex items-center gap-2">
                <p className="flex-1 text-xs text-slate-500">
                    {!dirty && (hasFile
                        ? 'No changes yet. Preview to check this file reads correctly.'
                        : 'No changes yet.')}
                    {dirty && !preview && 'Preview before saving — it is the only check on a mapping that reads the wrong column but valid values.'}
                    {dirty && preview && !preview.canActivate && 'Fix the problems above before saving.'}
                    {dirty && preview?.canActivate && 'Checked. Saving creates a new version.'}
                </p>
                <button
                    onClick={onPreview}
                    disabled={!hasFile || previewing}
                    title={hasFile ? undefined : 'Upload this file first'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md disabled:opacity-50"
                >
                    {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                    Preview
                </button>
                <button
                    onClick={onSave}
                    disabled={!canSave}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md disabled:opacity-50"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save version
                </button>
            </div>
        </div>
    );
}

function VersionHistory({ versions, onBack }) {
    return (
        <div className="flex-1 overflow-y-auto px-5 py-4">
            <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-3"
            >
                <ArrowLeft size={14} /> Back to mapping
            </button>
            {!versions && <Centered><Loader2 className="animate-spin" /> Loading…</Centered>}
            {versions?.length === 0 && <p className="text-sm text-slate-500">No versions.</p>}
            <ul className="space-y-2">
                {(versions ?? []).map((v) => (
                    <li key={v.version} className="border border-slate-200 rounded-md px-3 py-2">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900">v{v.version}</span>
                            {v.inForce && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                    in force
                                </span>
                            )}
                        </div>
                        {v.note && <p className="text-xs text-slate-600 mt-0.5">{v.note}</p>}
                        <p className="text-xs text-slate-400 mt-0.5">
                            {v.savedBy ?? 'Built in'}
                            {v.savedAt && ` · ${new Date(v.savedAt).toLocaleString()}`}
                        </p>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * What the operator is choosing from, which is not the same for all four files.
 * Two are plain tables, one is only an invoice, and the lender trail file is
 * both — a table of loans on one sheet and an invoice on another. Saying
 * "columns" to someone looking at an invoice is simply wrong, and it is the
 * kind of wrong that makes people doubt the rest of the screen.
 */
function sourceNoun(shape) {
    if (shape === 'INVOICE') return 'row labels';
    if (shape === 'BOTH') return 'column headings and row labels';
    return 'column headings';
}

const Centered = ({ children }) => (
    <div className="flex-1 flex items-center justify-center gap-2 text-sm text-slate-500">
        {children}
    </div>
);

const Notice = ({ tone, children }) => (
    <div className={`mx-5 my-3 flex items-start gap-2 text-sm rounded-md px-3 py-2 border ${
        tone === 'error'
            ? 'bg-rose-50 border-rose-200 text-rose-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'}`}
    >
        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
        <span className="whitespace-pre-line">{children}</span>
    </div>
);

/**
 * The server's sentence when there is one. Its refusals name what will happen
 * rather than which rule was broken, so they are worth showing verbatim.
 */
function messageOf(err) {
    return err?.response?.data?.message ?? err?.message ?? 'Something went wrong.';
}
